import React, {useState} from "react";
import {useLocation, useParams} from "react-router-dom";
import {useAsync} from "react-async-hook";
import {LabelSetForm, LabelSetFormValues} from "../../components/pages/admin/LabelSet/LabelSetForm";
import {useSystemMessage} from "../../hooks/useSystemMessage";
import useAppRouter from "../../hooks/useAppRouter";
import {labelSetApi} from "../../apis/admin/menu/labelSetApi";
import useLoading from "../../hooks/useLoading";
import {ApiClientError, ResponseCode} from "../../apis/errors";
import {logError} from "../../libs/utils/logError";

type UrlParams = {
  id: string;
}

export const LabelSetEditPage: React.FC = () => {
  const [defaultValues, setDefaultValues] = useState<LabelSetFormValues>({
    name: '',
    content: '',
    labelList: [],
  });
  const [ isLoadedDefaultValues, setIsLoadedDefaultValues ] = useState<boolean>(false);
  const { success } = useSystemMessage();
  const [ abortController, setAbortController ] = useState<AbortController | undefined>();
  const [ apiClientError, setApiClientError ] = useState<ApiClientError | null>(null);
  const { goto } = useAppRouter();
  const location = useLocation();
  const withLoading = useLoading();
  const { id } = useParams<UrlParams>();
  const labelSetId: number = parseInt(id, 10);

  useAsync(async () => {
    const fetch = async () => {
      setAbortController(new AbortController());
      try
      {
        const response = await labelSetApi.show(labelSetId, { abortSignal: abortController?.signal });
        setAbortController(undefined);
        setDefaultValues({
          name: response.labelSet.name,
          content: response.labelSet.content,
          labelList: response.labelSet.labelList,
        });
        setAbortController(undefined);
        setIsLoadedDefaultValues(true);

      } catch(e) {
        await logError(
          location.pathname,
          'ラベルセット詳細の取得に失敗しました。',
          e
        );

        if ((e instanceof ApiClientError) && (e.code === ResponseCode.NOT_FOUND)) {
          goto('notFoundError');
          return;
        }

        goto('internalServerError');
      }
    };

    withLoading(fetch);
  }, []);

  const clearApiClientError = async (): Promise<void> => {
    setApiClientError(null);
  }

  const onSave = async (data: LabelSetFormValues): Promise<void> => {
    setAbortController(new AbortController());
    try {
      await labelSetApi.update(
        labelSetId,
        {
          name: data.name,
          content: data.content,
          labelList: data.labelList,
        },
        { abortSignal: abortController?.signal });

      setAbortController(undefined);
      await success('ラベルセットを更新しました。');
      goto('labelSetList');

    } catch (e) {
      await logError(
        location.pathname,
        'ラベルセットの更新に失敗しました。',
        e
      );

      if (e instanceof ApiClientError) {
        setApiClientError(e);
        return;
      }

      goto('internalServerError');
    }
  }

  return (
    <>
      {isLoadedDefaultValues &&
        (
          <LabelSetForm
            {...{
              defaultValues,
              isEdit: true,
              apiClientError,
              clearApiClientError,
              onSave,
            }}
          />
        )
      }
    </>
  );
}
